import type {AnalyticsInstance, AnalyticsPlugin} from 'analytics';
import Analytics from 'analytics';

import clickTracker from './plugins/clickTracker';
import contextExtractor from './plugins/contextExtractor';
import rudderstackEvents from './plugins/rudderstackEvents';
import type {AnalyticsContext, RudderstackConfig, ServiceConfig} from './shared/types';

export interface EzcaterAnalyticsConfig {
  service: ServiceConfig;
  rudderstack: RudderstackConfig;
  clickTracking?: boolean;
}

// @analytics/core appends every page/track event to `state.*.history` arrays that are never
// cleared, causing unbounded memory growth in long-lived instances (e.g. SPAs where the
// analytics instance persists across many navigations).
// See: https://github.com/DavidWells/analytics/issues/358
//
// These reducers replace the defaults to retain only the most-recent event.
// The `analytics` package types don't expose `reducers`, but @analytics/core supports it.
type HistoryReducerState = {last: Record<string, unknown>; history: unknown[]};
type ReducerAction = {type: string; [key: string]: unknown};

function pageReducer(
  state: HistoryReducerState = {last: {}, history: []},
  action: ReducerAction,
): HistoryReducerState {
  if (action.type === 'page') {
    return {...state, last: {properties: action.properties, meta: action.meta}};
  }
  return state;
}

function trackReducer(
  state: HistoryReducerState = {last: {}, history: []},
  action: ReducerAction,
): HistoryReducerState {
  if (action.type === 'track') {
    return {
      ...state,
      last: {event: action.event, properties: action.properties, meta: action.meta},
    };
  }
  return state;
}

export function createEzcaterAnalytics(
  config: EzcaterAnalyticsConfig,
  getContext?: () => AnalyticsContext | undefined,
): AnalyticsInstance {
  const {rudderstack: rudderstackConfig} = config;
  // contextExtractor must precede rudderstackEvents: it enriches page/track payloads
  // with service, eventClient, cloudflare, and request context before delivery.
  const plugins: AnalyticsPlugin[] = [
    contextExtractor({service: config.service}, getContext ?? (() => undefined)),
    rudderstackEvents(rudderstackConfig),
    ...(config.clickTracking ? [clickTracker()] : []),
  ];

  return Analytics({
    app: 'ezcater',
    plugins,
    reducers: {page: pageReducer, track: trackReducer},
  } as unknown as Parameters<typeof Analytics>[0]);
}
